import { useState } from 'react';
import { useNotify, useRefresh, useRedirect } from 'react-admin';
import auth from '../../auth/auth0';
import { useApiConfig } from '../../services/apiConfig';
import { leadImportFields, leadImportStatuses } from '../leadImports.model';
import { canCancelJob } from '../utils/timeUtils';
import logger from '../../utils/logger';
/**
 * Custom hook to manage lead import actions
 * 
 * @param {Object} record - The lead import record
 * @returns {Object} States and functions to manage lead import actions
 */
const useLeadImportActions = (record) => {
  // States for dialogs
  const [showLogs, setShowLogs] = useState(false);
  const [showRollbackDialog, setShowRollbackDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  
  // States for loading indicators
  const [isRollbackLoading, setIsRollbackLoading] = useState(false);
  const [isCancelLoading, setIsCancelLoading] = useState(false);
  const [isRetryLoading, setIsRetryLoading] = useState(false);
  
  // React-Admin hooks
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  const apiConfig = useApiConfig();

  // Capability checks
  const canViewLogs = record && record[leadImportFields.status] !== leadImportStatuses.QUEUED;
  const canRollback = record && [leadImportStatuses.FAILED, leadImportStatuses.CANCELLED, leadImportStatuses.ROLLBACK_FAILED].includes(record[leadImportFields.status]) && record[leadImportFields.startedAt] != null;
  const canRetryJob = record && [leadImportStatuses.FAILED, leadImportStatuses.CANCELLED].includes(record[leadImportFields.status]);
  const canCancelNow = canCancelJob(record);
  const showCancel = record && [leadImportStatuses.QUEUED, leadImportStatuses.PROCESSING].includes(record[leadImportFields.status]);

  // Check for download display
  const failedCount = record ? (record[leadImportFields.failedRecords] || 0) : 0;
  const duplicatedCount = record ? (record[leadImportFields.duplicatedRecords] || 0) : 0;
  const shouldShowDownload = record && (
    record[leadImportFields.status] === leadImportStatuses.FAILED ||
    failedCount > 0 ||
    duplicatedCount > 0
  );

  /**
   * Show import details
   */
  const handleShowDetails = async () => {
    // Force refresh of the data before redirecting
    refresh();
    
    // Small delay to allow the refresh to complete
    setTimeout(() => {
      redirect('show', 'lead-imports', record.id);
    }, 100);
  };

  /**
   * Download detailed report
   */
  const handleDownloadReport = async () => {
    notify('Downloading report...Please wait', { type: 'info' });

    if (!record) return;

    if (shouldShowDownload) {
      try {
        const response = await fetch(
          `${apiConfig.getApiUrl('leadImports')}/lead-imports/${record.id}/detailed-report`,
          {
            headers: {
              Authorization: `Bearer ${auth.getIdToken()}`,
            },
          },
        );

        if (response.ok) {
          const data = await response.json();
          window.open(data.downloadUrl, '_blank');
          notify('Detailed report download started', { type: 'success' });
        } else {
          throw new Error(
            `Failed to get detailed report: ${response.statusText}`,
          );
        }
      } catch (error) {
        logger.error('Download error:', error);
        notify(`Download failed: ${error.message}`, { type: 'error' });
      }
    }
  };

  /**
   * Show processing logs
   */
  const handleViewLogs = () => {
    setShowLogs(true);
  };

  /**
   * Close processing logs
   */
  const handleCloseLogs = () => {
    setShowLogs(false);
  };

  /**
   * Open rollback dialog
   */
  const handleRollback = () => {
    setShowRollbackDialog(true);
  };

  /**
   * Confirm rollback
   */
  const handleRollbackConfirm = async () => {
    if (!record?.id) {
      notify('No job selected for rollback', { type: 'error' });
      return;
    }
    
    setIsRollbackLoading(true);
    
    try {
      const response = await fetch(
        `${apiConfig.getApiUrl('leadImports')}/lead-imports/${record.id}/rollback`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth.getIdToken()}`,
          },
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }
      
      notify('Rollback started — progress will update automatically.', { type: 'success' });
      
      refresh();
      setShowRollbackDialog(false);
      
    } catch (error) {
      logger.error('Rollback job error:', error);
      notify(`Failed to rollback job: ${error.message}`, { type: 'error' });
    } finally {
      setIsRollbackLoading(false);
    }
  };

  /**
   * Retry a failed job
   */
  const handleRetry = async () => {
    if (!record?.id) {
      notify('No job selected for retry', { type: 'error' });
      return;
    }

    setIsRetryLoading(true);

    try {
      const response = await fetch(
        `${apiConfig.getApiUrl('leadImports')}/lead-imports/${record.id}/retry`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth.getIdToken()}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      notify('Job re-queued for processing.', { type: 'success' });
      refresh();

    } catch (error) {
      logger.error('Retry job error:', error);
      notify(`Failed to retry job: ${error.message}`, { type: 'error' });
    } finally {
      setIsRetryLoading(false);
    }
  };

  /**
   * Open cancel dialog
   */
  const handleCancel = () => {
    setShowCancelDialog(true);
  };

  /**
   * Confirm cancellation
   */
  const handleCancelConfirm = async () => {
    if (!record?.id) {
      notify('No job selected for cancellation', { type: 'error' });
      return;
    }
    
    setIsCancelLoading(true);
    
    try {
      const response = await fetch(
        `${apiConfig.getApiUrl('leadImports')}/lead-imports/${record.id}/cancel`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth.getIdToken()}`,
          },
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }
      
      notify('Job cancelled successfully', { type: 'success' });
      
      refresh();
      setShowCancelDialog(false);
      
    } catch (error) {
      logger.error('Cancel job error:', error);
      notify(`Failed to cancel job: ${error.message}`, { type: 'error' });
    } finally {
      setIsCancelLoading(false);
    }
  };

  return {
    // États
    showLogs,
    showRollbackDialog,
    showCancelDialog,
    isRollbackLoading,
    isCancelLoading,
    
    // Capacités
    canViewLogs,
    canRollbackJob: canRollback,
    canRetryJob,
    canCancelNow,
    showCancel,
    shouldShowDownload,
    isRetryLoading,

    // Actions
    handleShowDetails,
    handleDownloadReport,
    handleViewLogs,
    handleCloseLogs,
    handleRollback,
    handleRollbackConfirm,
    handleRetry,
    handleCancel,
    handleCancelConfirm,
    
    // Fermeture des dialogues
    closeRollbackDialog: () => setShowRollbackDialog(false),
    closeCancelDialog: () => setShowCancelDialog(false),
  };
};

export default useLeadImportActions;
